import React, { useEffect, useState } from "react";
import { createStyles, Flex, NumberInput, Select } from "@mantine/core";
import { UnitType } from "../../types/common";

interface IDynamicUnitInput {
	min: number;
	value: number;
	onChange: (value: number) => void;
	unit: string;
	unitType: UnitType;
	className?: string;
	max?: number | undefined;
	step?: number | undefined;
	precision?: number | undefined;
}

const UNIT_TYPES: Record<UnitType, string[]> = {
	weight: ["g", "kg", "t"],
	distance: ["mm", "cm", "m", "km"],
	time: ["ms", "s", "min", "h"],
};

const CONVERSION_FACTORS: Record<UnitType, Record<string, number>> = {
	weight: {
		g: 0.001,
		kg: 1,
		t: 1000,
	},
	distance: {
		mm: 0.001,
		cm: 0.01,
		m: 1,
		km: 1000,
	},
	time: {
		ms: 0.001,
		s: 1,
		min: 60,
		h: 3600,
	},
};

const DynamicUnitInput: React.FC<IDynamicUnitInput> = ({ className, min, max, value, step, precision, onChange, unit, unitType }) => {
	const { classes } = useStyles();
	const [currentValue, setCurrentValue] = useState(value);
	const [selectedUnit, setSelectedUnit] = useState<string>(unit);

	useEffect(() => {
		if (!unit || !unitType) {
			console.warn("DynamicUnitInput: unit or unitType is not defined");
			return;
		}

		const validUnits = UNIT_TYPES[unitType];
		if (!validUnits.includes(unit)) {
			console.warn(
				`DynamicUnitInput: unit "${unit}" is not valid for unitType "${unitType}". Valid units are: ${validUnits.join(", ")}`
			);
		}
	}, [unit, unitType]);

	// Returns the value converted to base unit and the value for selected unit
	const parseInput = (input: string, castedUnit: string): [number, number] => {
		if (!unit || !unitType) return [parseFloat(input), parseFloat(input)];

		const numberPart = parseFloat(input);

		// Convert to base unit first (for weight base is kg etc.)
		const baseValue = numberPart * CONVERSION_FACTORS[unitType][castedUnit];

		// Then convert to desired unit
		const factor = CONVERSION_FACTORS[unitType][castedUnit];
		const castedValue = baseValue / factor;

		return [clampValue(baseValue, unit), clampValue(castedValue, castedUnit)];
	};

	const castValueToSelectedUnit = (currentValue: number, currentUnit: string, castedUnit: string): number => {
		if (!unit || !unitType) return currentValue;
		const baseValue = currentValue * CONVERSION_FACTORS[unitType][currentUnit];
		const factor = CONVERSION_FACTORS[unitType][castedUnit];
		return baseValue / factor;
	};

	const clampValue = (val: number, currentUnit: string) => {
		if (max === undefined) return val;

		const factor = CONVERSION_FACTORS[unitType][currentUnit];
		const maxInCurrentUnit = max / factor;
		const minInCurrentUnit = min / factor;

		return Math.max(Math.min(val, maxInCurrentUnit), minInCurrentUnit);
	};

	return (
		<Flex className={`${classes.wrapper} ${className}`}>
			<NumberInput
				min={min}
				max={max}
				value={currentValue}
				step={step}
				precision={precision}
				onBlur={(event) => {
					const [baseVal, castedVal] = parseInput(event.currentTarget.value, selectedUnit || unit);
					setCurrentValue(castedVal);
					onChange(baseVal);
				}}
				hideControls
			></NumberInput>

			<Select
				style={{ marginLeft: 8, width: 80 }}
				data={UNIT_TYPES[unitType].map((u) => ({ value: u, label: u }))}
				value={selectedUnit}
				onChange={(val) => {
					const [baseVal, castedVal] = parseInput(
						castValueToSelectedUnit(currentValue, selectedUnit, val as string).toString(),
						val as string
					);
					setSelectedUnit(val as string);
					setCurrentValue(castedVal);
					onChange(baseVal);
				}}
			></Select>
		</Flex>
	);
};

const useStyles = createStyles((theme) => ({
	wrapper: {
		alignItems: "center",
	},
}));

export default DynamicUnitInput;
